import { MockTestWebsocket } from '../../modules/server/MockTestWebsocket';
import { PAGE_TYPE } from '../base/app/constants';
import { toggleLoad } from '../base/loading';
import { checkOccupiedUser, getExternalUserId, getIsTeacher } from '../base/local';
import { checkSurveyString, objectToQueryString, splitAnswers } from '../base/question/functions';
import { getAnswerList, getAssignmentList, getLoadAnswer, getQuestionList, getSubmitStatus, getSurveyFrequency, getTestTime, postSaveSubmitAnswer, postSubmitAnswer, startTimer, } from '../base/question/querys';
import { MiddlewareRegistry } from '../base/redux';
import { setCountDownSet } from '../base/settings';
import { LEAVE_ROOM, SET_ROOM_JOINED, getRoomOption } from '../room';
import { onRefetchTestList, onTestSubmitted, setAnswerList, setFocusUser, setIsEnterRoom, setIsMockMode, setNotSubmitUser, setOmrFrequency, setQuestionList, setSubmitList, setSurveyFrequency, setSurveySave, setTestBackground, setTestList, setTestType, } from './action';
import { ON_ENTER_TEST_ROOM, ON_REFETCH_TEST_LIST, ON_TEST_SUBMITTED, SET_FOCUS_TEST_ID, SET_FOCUS_USER } from './actionTypes';
import { getFocusTestId, getFocusUser, getIsEnterTestRoom, getIsMockMode, getMergeSurveySaveById, getOmrFrequencyByTestId, getQuestions, getStatisticsOption, getTestItemByTestId, getTestList, } from './functions';
const getSubmitStatusAPI = async (test_id, member_id, statisticsOption) => {
    // 제출 현황
    const submitStatusResponse = await getSubmitStatus({
        id: test_id,
        props: { member_id, ...statisticsOption },
    });
    APP.store.dispatch(setSubmitList(test_id, submitStatusResponse.result));
};
// 제출 신호(교사) 부분 갱신 코얼레싱 — 연속 제출(수십 명 러시) 시 테스트당 1초 고정창으로
// 병합해 제출 건수만큼 REST 가 나가는 것을 막는다. (전체 initTest 재조회 대신 제출현황 1콜만)
// 고정창 스로틀: 창 안의 후속 제출은 무시(창 종료 시 1회 조회가 그 시점 최신을 반영) —
// 트레일링 디바운스는 러시가 지속되는 동안 갱신이 무기한 밀리므로 쓰지 않는다.
const submitStatusRefreshTimers = new Map();
const clearSubmitStatusRefreshTimers = () => {
    submitStatusRefreshTimers.forEach(timer => clearTimeout(timer));
    submitStatusRefreshTimers.clear();
};
const scheduleSubmitStatusRefresh = (store, test_id) => {
    if (!test_id) {
        // 어떤 테스트인지 모르면 목록 전체 재조회로 폴백(무로딩)
        store.dispatch(onRefetchTestList());
        return;
    }
    if (submitStatusRefreshTimers.has(test_id))
        return; // 창 내 후속 제출 병합
    submitStatusRefreshTimers.set(test_id, setTimeout(async () => {
        submitStatusRefreshTimers.delete(test_id);
        try {
            const statisticsOption = getStatisticsOption(store.getState);
            await getSubmitStatusAPI(test_id, '', statisticsOption);
            // 설문형이면 응답 빈도도 함께 갱신(교사 화면 노출 데이터)
            const item = getTestList(store.getState).find((t) => t.id === test_id);
            if (item?.type === '설문형') {
                const res = await getSurveyFrequency(test_id);
                if (res)
                    store.dispatch(setSurveyFrequency(test_id, res.result));
            }
        }
        catch (e) {
            console.error('제출 현황 부분 갱신 실패:', e);
        }
    }, 1000));
};
// 동일 (test_id, member_id) 에 대한 getAnswerState 중복 조회 병합용 in-flight 집합.
// initTest 말미 조회와 setTestList→SET_FOCUS_USER 캐스케이드가 같은 대상을 거의 동시에 부르며
// submit-status 가 3회 중 2회 중복되던 것을 1회로 합친다. (내부 재시도 _isRetry 는 통과)
const answerStateInFlight = new Set();
const getAnswerState = async (test_id, member_id, retryLeft = 0, _isRetry = false, retryUnsubmitted = false) => {
    const inflightKey = `${test_id}:${member_id}`;
    if (!_isRetry) {
        if (answerStateInFlight.has(inflightKey))
            return; // 동일 대상 중복 진입 병합
        answerStateInFlight.add(inflightKey);
    }
    try {
        await getSubmitStatus({
            id: test_id,
            props: { member_id },
        }).then(async (response) => {
            const { total_count, items } = response?.result ?? {};
            const item = total_count && items ? items.find((i) => i.member_id === member_id) : undefined;
            // 서버가 제출을 확정 반영한 경우: 실제 답안(/answers) 로드 + 전체 item 반영 후 종료.
            if (item?.not_submitted === false) {
                const answerQueryString = objectToQueryString({
                    test_id: test_id,
                    from: member_id,
                    orderby: 'value',
                });
                await getAnswerList(answerQueryString).then((answerResponse) => {
                    let answerList = [];
                    if (answerResponse) {
                        const { items } = answerResponse.result;
                        answerList = items;
                    }
                    APP.store.dispatch(setAnswerList(answerList));
                });
                APP.store.dispatch(setNotSubmitUser(item));
                return;
            }
            // 본인(학생) 요청인지 판별 — 교사가 특정 학생을 조회(SET_FOCUS_USER)하는 경우엔
            // 아래 낙관적/세션마커 보정을 적용하지 않는다(다른 학생 상태 오염 방지).
            const isSelf = member_id === getExternalUserId(APP.store.getState);
            const currentSubmit = APP.store.getState()['features/test'].selectUserSubmit;
            let submittedHint = isSelf && currentSubmit?.not_submitted === false;
            if (isSelf && !submittedHint) {
                try {
                    if (sessionStorage.getItem('mimac_submitted:' + test_id) === '1') {
                        submittedHint = true;
                    }
                }
                catch (e) { }
            }
            if (submittedHint) {
                // 이 클라이언트가 제출에 성공했음을 아는 상태(낙관적 갱신 또는 세션 마커).
                // submit-status 읽기가 아직 제출을 반영 못했더라도(read-after-write 지연) 미제출로
                // 되돌리지 않는다. 재진입(리셋으로 undefined)이면 제출완료로 복원해 'loading'/빈 OMR 을
                // 방지하고, 실제 답안/점수는 남은 재시도로 submit-status 가 일관될 때 채운다.
                if (currentSubmit?.not_submitted !== false) {
                    APP.store.dispatch(setNotSubmitUser({ member_id, not_submitted: false }));
                }
                if (retryLeft > 0) {
                    setTimeout(() => {
                        getAnswerState(test_id, member_id, retryLeft - 1, true, retryUnsubmitted);
                    }, 700);
                }
                return;
            }
            // 전체 타이머 종료 직후(retryUnsubmitted)엔 서버 강제제출이 아직 submit-status 에 반영되기 전
            // (read-after-write 지연)일 수 있으므로, 재시도가 남았으면 미제출로 단정하지 말고 재폴링한다.
            // 제출완료 확정은 위 not_submitted===false(서버 값) 분기에서만 일어나 가짜 제출완료를 만들지 않는다.
            if (retryUnsubmitted && retryLeft > 0) {
                setTimeout(() => {
                    getAnswerState(test_id, member_id, retryLeft - 1, true, retryUnsubmitted);
                }, 700);
                return;
            }
            // 진짜 미제출: 기존 동작 유지(마커로 'loading' 해제, 답안 비움).
            APP.store.dispatch(setNotSubmitUser(item ?? { member_id, not_submitted: true }));
            APP.store.dispatch(setAnswerList([]));
        });
    }
    finally {
        if (!_isRetry)
            answerStateInFlight.delete(inflightKey);
    }
};
const initTest = async (store, roomId, isOnlyTestMode, retryUnsubmitted = false, retryTestId) => {
    const queryString = objectToQueryString({
        conference_id: roomId,
        // ...(isTeacher ? {} : { status: ['open', 'closed'] }),
    });
    let testBackgroundUrl = undefined;
    let testItem = undefined;
    let testList = [];
    let testStatistics = [];
    let testSubmitStatus = [];
    // conference 기본 정보 조회. with_more_info=true 는 result.items[].members 배열을 포함시키느라
    // 응답이 ~10배 느려지는데(서버 확인), 여기서 읽는 값은 count_down_set/type/background 뿐이고
    // members 는 어디서도 쓰지 않으므로 붙이지 않는다.
    const conferenceBackAPI = `/api/mimac/v1/conferences?id=${roomId}`;
    const response = await fetch(conferenceBackAPI);
    const result = await response.json();
    const { total_count: conference_count, items: conference_items } = result.result;
    APP.store.dispatch(setCountDownSet(conference_items[0]?.count_down_set || []));
    // 모의고사 모드 판별: conference type === 'mock_test'.
    // (입장/재조회 모든 경로에서 conference를 조회하므로, isOnlyTestMode 파라미터가 아닌 실제 conference
    //  type으로 판별해야 refetch(initTest false) 시에도 모의고사 여부가 유지됨.)
    // 진행시간 표시를 모의고사(정적) vs 일반(실시간)으로 분기하는 데 사용.
    store.dispatch(setIsMockMode(conference_items?.[0]?.type === 'mock_test'));
    if (isOnlyTestMode) {
        if (conference_count > 0) {
            const item = conference_items[0];
            if (item.type === 'mock_test') {
                testBackgroundUrl = item.background;
            }
        }
        else {
            return;
        }
    }
    // Assginment 리스트 가져오기
    const assignmentResponse = await getAssignmentList(queryString);
    const { total_count, items } = assignmentResponse.result;
    if (total_count === 0) {
        return { testBackgroundUrl, testItem, testList, testStatistics, testSubmitStatus };
    }
    const member_id = getExternalUserId(store.getState);
    // 모든 비동기 작업을 병렬로 실행하기 위한 프로미스 배열
    const promises = items.map(async (item) => {
        store.dispatch(setTestType(item.type));
        const questionQueryString = objectToQueryString({
            test_id: item.id,
            orderby: ["IF(subject_item='공통',0,1)", 'subject_item', 'number'],
        });
        const questionListResponse = await getQuestionList(questionQueryString);
        // fetchData 는 실패 시 undefined 를 반환하므로 .result 직접 접근은 throw 위험 → 가드
        const { total_count: questionTotalCount, items: questionItems } = questionListResponse?.result ?? {};
        if (questionTotalCount > 0) {
            store.dispatch(setQuestionList(item.id, questionItems));
        }
        // (제거) 통계 getStatistics — 저장만 되고 읽는 곳이 0곳인 죽은 요청이었음.
        // 테스트당 직렬 REST 1회를 입장 임계경로에서 덜어낸다(운영 부하 감사 Q2).
        // 제출 현황(submitList). 소비처는 교사 제출현황 테이블 + mock 테스트룸 학생 자동종료
        // 리스너(getIsEnterTestRoom 게이트)뿐 — 라이브 강의실 학생은 submitList 를 쓰지 않고
        // 본인 상태는 아래 getAnswerState(selectUserSubmit)로 받으므로 불필요한 submit-status 조회를 생략한다.
        const statisticsOption = getStatisticsOption(store.getState);
        const isTeacher = getIsTeacher(store.getState);
        if (isTeacher || getIsEnterTestRoom(store.getState)) {
            getSubmitStatusAPI(item.id, isTeacher ? '' : member_id || '', statisticsOption);
        }
        // 설문형일 경우 설문 빈도 조회
        if (item.type === '설문형') {
            // 설문형에 대한 추가 로직
            if (!getIsTeacher(store.getState)) {
                const loadAnswerResponse = await getLoadAnswer({
                    id: item.id,
                    props: {
                        member_id,
                    },
                });
                const { subject_items, answers } = loadAnswerResponse?.result ?? {};
                await store.dispatch(setSurveySave(item.id, {
                    server: { subject_items: subject_items || [], answers: answers || [] },
                }));
            }
            else {
                getSurveyFrequency(item.id).then((res) => {
                    if (res) {
                        const frequencies = res.result;
                        store.dispatch(setSurveyFrequency(item.id, frequencies));
                    }
                });
            }
        }
        // 파일첨부형일 경우의 처리
        else if (item.type === '파일첨부형') {
            // 필요한 경우 이 블록에 추가 작업을 구현
        }
        else {
            if (!getIsTeacher(store.getState)) {
                const loadAnswerResponse = await getLoadAnswer({
                    id: item.id,
                    props: {
                        member_id,
                    },
                });
                const { subject_items, answers } = loadAnswerResponse?.result ?? {};
                await store.dispatch(setOmrFrequency(item.id, {
                    server: { subject_items: subject_items || [], answers: answers || [] },
                }));
            }
        }
    });
    // 모든 비동기 작업을 기다림
    await Promise.all(promises);
    testItem = isOnlyTestMode ? items[0] : items.find((i) => i.status === 'open');
    testList = items;
    // 모의고사: 개별 타이머가 이미 생성된(서버 getTestTime의 common===false) 테스트는
    // 재입장(세션 초기화로 sessionStorage 표식 소실)에도 진행 중 화면으로 복원되도록 표식을 서버 기준으로 세팅.
    // (common===true = 전체 타이머 = 개별 타이머 없음 → 표식 미설정)
    if (!getIsTeacher(store.getState) && conference_items?.[0]?.type === 'mock_test') {
        await Promise.all(items
            .filter((it) => it.status === 'open')
            .map(async (it) => {
            try {
                const timeRes = await getTestTime(it.id);
                if (timeRes?.result?.common === false) {
                    try {
                        sessionStorage.setItem(`mock_personal_timer:${it.id}`, '1');
                    }
                    catch (e) { }
                    // 재입장(ON_ENTER_TEST_ROOM = isOnlyTestMode) 시: 퇴장 때 일시정지된 개별 타이머를 재개.
                    // (타이머 시작 API = startTimer, body 없음. refetch 등 그 외 경로에서는 재개하지 않음)
                    if (isOnlyTestMode) {
                        try {
                            await startTimer({ id: it.id });
                        }
                        catch (e) { }
                    }
                }
            }
            catch (e) { }
        }));
    }
    store.dispatch(setTestBackground(testBackgroundUrl));
    // 학생: 본인 제출 상태(selectUserSubmit)를 채워 QuestionSolve 의 'loading' 게이트를 해제한다.
    // getIsSubmitUser 는 학생일 때 selectUserSubmit 이 undefined 면 'loading' 을 반환하는데,
    // selectUserSubmit 을 채우는 경로가 교사(SET_FOCUS_USER/Test.Submitted)에만 있어 학생이
    // 진행 중 테스트에 입장하면 영구히 'loading' 에 머물렀다. initTest 는 입장/시작·제출 후 모두
    // 실행되므로 여기서 호출하면 전 시나리오가 커버된다.
    // ★setTestList 앞에서 fire-and-forget 으로 먼저 in-flight 를 잡는다 → 직후 setTestList 가
    // 유발하는 SET_FOCUS_USER 캐스케이드의 동일 조회(submit-status 중복 3회 중 1회)가 병합 생략되고,
    // 재시도(retryLeft=4)가 있는 이쪽 호출이 살아남는다.
    // ★Test.Finished 발 재조회에서는 대상 테스트가 이미 closed 라 items.find(open)=undefined →
    // 기존엔 getAnswerState 가 아예 호출되지 않아 selectUserSubmit 이 갱신되지 못하고 '미제출'로
    // 남았다. 종료 재조회(retryUnsubmitted)일 땐 payload 의 test_id(폴백: closed 테스트)로 직접
    // 조회하고, 서버 일괄 강제제출 반영 지연을 감안해 재시도 예산을 8회(≈5.6s)로 늘린다.
    // 모의고사(isOnlyTestMode/isMockMode)는 이 폴백을 타지 않는다(retryUnsubmitted 가 항상 false).
    if (!getIsTeacher(store.getState)) {
        let answerTarget = testItem;
        if (!answerTarget && retryUnsubmitted && !isOnlyTestMode) {
            answerTarget =
                (retryTestId && items.find((i) => i.id === retryTestId)) ||
                    items.find((i) => i.status === 'closed');
        }
        if (answerTarget) {
            getAnswerState(answerTarget.id, member_id, retryUnsubmitted ? 8 : 4, false, retryUnsubmitted);
        }
    }
    store.dispatch(setTestList(testList));
    return testList;
};
const handlerMessage = async (data) => {
    switch (data.class) {
        case 'Room.Member.Session.Add':
            // data.status === 200 && setJoin(true);
            // 라이브 테스트 종료 전 알림 시간 설정
            // APP.store.dispatch(updateRoomPolicy(data.payload.policy, true));
            break;
        case 'Meeting.Meeting.Closed':
        case 'MIMAC.Conference.Finished':
            APP.store.dispatch(setIsEnterRoom(false));
            window.close();
            break;
        case 'Room.Member.Session.Remove':
            APP.store.dispatch(setIsEnterRoom(false));
            break;
        case 'Event.Room.Member.Kicked':
            //alert('같은 아이디로 접속되어 퇴장 되었습니다 아이디를 확인해 주세요');
            APP.store.dispatch(setIsEnterRoom(false));
            break;
        case 'Meeting.Meeting.Signaled':
            const { payload } = data.payload;
            switch (payload.class) {
                case 'MIMAC.Test.Started':
                    APP.store.dispatch(onRefetchTestList());
                    APP.store.dispatch(toggleLoad(false));
                    break;
                case 'MIMAC.Test.Submitted':
                    // 강의실(mate) 경로와 동일하게 역할별 최소 처리로 통일.
                    // (교사=제출현황 부분갱신+열람 학생 답안, 학생=본인 제출만 재조회, 남=무시)
                    APP.store.dispatch(onTestSubmitted(payload));
                    break;
                case 'MIMAC.Test.Finished':
                    // 전체 타이머 종료(서버 강제제출) 후, 학생(비모의고사)은 서버 반영이 늦을 수 있어
                    // 재조회를 재폴링(retryUnsubmitted)으로 요청 → 새로고침 없이 채점결과로 자동 전환.
                    APP.store.dispatch(onRefetchTestList(false, !getIsTeacher(APP.store.getState) && !getIsMockMode(APP.store.getState), payload?.test_id));
                    if (getIsTeacher(APP.store.getState)) {
                        APP.store.dispatch(toggleLoad(false));
                        break;
                    }
                    APP.store.dispatch(toggleLoad(true));
                    const test_id = payload?.test_id;
                    const member_id = getExternalUserId(APP.store.getState);
                    try {
                        await getSubmitStatus({
                            id: test_id,
                            props: { member_id },
                        }).then(async (response) => {
                            const { total_count, items } = response.result;
                            if (total_count > 0) {
                                const item = items.find((i) => i.member_id === member_id);
                                if (item.not_submitted) {
                                    const testItem = getTestItemByTestId(APP.store.getState(), test_id);
                                    if (testItem.type === '설문형') {
                                        const surveyData = getMergeSurveySaveById(APP.store.getState, test_id);
                                        // 서버 contract: answers는 제출 대상 전체 문항을 포함해야 하며(빈 배열이면 400),
                                        // 미입력 문항도 빈 값(values:[''])으로 보낸다. 설문 전체 문항(questionList)을 기준으로
                                        // 입력값(surveyData.answers)이 있으면 그대로, 없으면 빈 값으로 빌드한다.
                                        const surveyInput = splitAnswers(surveyData.answers) || [];
                                        // 무입력이면 강제제출 스킵 — 테스트가 이미 closed라 서버가 403(진행 중이 아닌
                                        // 테스트)으로 거절하고, 정책상 무입력은 미제출로 남긴다.
                                        const hasSurveyAnswer = surveyInput.some((a) => Array.isArray(a.values) &&
                                            a.values.some((v) => (typeof v === 'string' ? v.trim() !== '' : v != null)));
                                        if (!hasSurveyAnswer) {
                                            try {
                                                sessionStorage.setItem('mimac_submit_reason:' + test_id, 'timeup_unsubmitted');
                                            }
                                            catch (e) { }
                                            return;
                                        }
                                        const surveyQuestionList = getQuestions(APP.store.getState, test_id);
                                        const surveyAnswers = surveyQuestionList.map((q) => {
                                            const answer = surveyInput.find((a) => a.question_id === q.id);
                                            return answer || { question_id: q.id, values: [''] };
                                        });
                                        // 시간차 중복 보조 가드: 다른 경로(컨테이너 자동제출/수동 제출)가 이미 제출에 성공해
                                        // 마커를 남겼으면 강제제출을 스킵. (60ms 동시 레이스는 postSubmitAnswer 초크포인트가 병합)
                                        try {
                                            if (sessionStorage.getItem('mimac_submitted:' + test_id) === '1')
                                                return;
                                        }
                                        catch (e) { }
                                        await postSubmitAnswer({
                                            id: test_id,
                                            props: {
                                                subject_items: [],
                                                answers: surveyAnswers,
                                            },
                                        });
                                        // 설문 강제제출 완료 → 제출완료로 즉시 전환(기존 setFocusUser는 dispatch 없이 버려지던 값).
                                        if (!getIsMockMode(APP.store.getState)) {
                                            try {
                                                sessionStorage.setItem('mimac_submitted:' + test_id, '1');
                                            }
                                            catch (e) { }
                                            APP.store.dispatch(setNotSubmitUser({ member_id, not_submitted: false }));
                                            getAnswerState(test_id, member_id, 4);
                                        }
                                    }
                                    else if (testItem.type === '파일첨부형') {
                                    }
                                    else {
                                        // const subject_list =
                                        const omrFrequency = getOmrFrequencyByTestId(APP.store.getState(), test_id);
                                        // 무입력이면 강제제출 스킵 — closed 테스트 제출은 서버 403, 정책상 미제출 유지.
                                        const hasOmrAnswer = (omrFrequency?.answers || []).some((a) => Array.isArray(a.values) &&
                                            a.values.some((v) => (typeof v === 'string' ? v.trim() !== '' : v != null)));
                                        if (!hasOmrAnswer) {
                                            try {
                                                sessionStorage.setItem('mimac_submit_reason:' + test_id, 'timeup_unsubmitted');
                                            }
                                            catch (e) { }
                                            return;
                                        }
                                        const omrSubjectItems = omrFrequency.subject_items || [];
                                        // 서버 contract: answers는 제출 대상 전체 문항을 포함해야 하며(빈 배열이면 400),
                                        // 미입력 문항도 빈 값(values:[''])으로 보낸다. initTest가 저장한 전체 문항(questionList)을
                                        // 기준으로 빌드하되, 선택과목이 있는 테스트는 풀이화면(DetailQuestionTable)과 동일하게
                                        // 선택한 과목 + '공통' 문항만 제출 대상으로 필터링한다.
                                        const questionList = getQuestions(APP.store.getState, test_id);
                                        const submitQuestionList = omrSubjectItems && omrSubjectItems.length > 0
                                            ? questionList.filter((q) => omrSubjectItems.includes(q.subject_item) || q.subject_item === '공통')
                                            : questionList;
                                        const omrAnswers = submitQuestionList.map((q) => {
                                            const answer = (omrFrequency.answers || []).find((a) => a.question_id === q.id);
                                            return answer || { question_id: q.id, values: [''] };
                                        });
                                        // 시간차 중복 보조 가드: 다른 경로(컨테이너 자동제출/수동 제출)가 이미 제출에 성공해
                                        // 마커를 남겼으면 강제제출을 스킵. (60ms 동시 레이스는 postSubmitAnswer 초크포인트가 병합)
                                        try {
                                            if (sessionStorage.getItem('mimac_submitted:' + test_id) === '1')
                                                return;
                                        }
                                        catch (e) { }
                                        await postSubmitAnswer({
                                            id: test_id,
                                            props: {
                                                answers: omrAnswers,
                                                subject_items: omrSubjectItems,
                                            },
                                        });
                                        // 전체 타이머 종료 시 클라이언트가 강제제출을 마쳤으므로 제출완료로 즉시 전환한다.
                                        // (기존엔 이 갱신이 없어 화면이 '미제출'로 남고 새로고침해야 채점결과가 떴다.)
                                        // mimac_submitted 마커로 이후 재조회(레이스)도 미제출로 되돌지 않게 하고,
                                        // getAnswerState로 실제 답안/채점결과를 로드한다. 모의고사(개별 타이머)는 자체 흐름이라 제외.
                                        if (!getIsMockMode(APP.store.getState)) {
                                            try {
                                                sessionStorage.setItem('mimac_submitted:' + test_id, '1');
                                            }
                                            catch (e) { }
                                            APP.store.dispatch(setNotSubmitUser({ member_id, not_submitted: false }));
                                            getAnswerState(test_id, member_id, 4);
                                        }
                                    }
                                }
                            }
                        });
                    }
                    catch (error) {
                    }
                    finally {
                        APP.store.dispatch(toggleLoad(false));
                    }
                    break;
            }
            break;
    }
};
let disconnectWebsocketList = [];
let alreadyDisconnect = false;
export const setDisconnectWebsocketList = (list) => {
    disconnectWebsocketList = list;
};
export const disconnectWebsocket = async (e) => {
    if (alreadyDisconnect)
        return;
    try {
        disconnectWebsocketList.forEach(async (test_id) => {
            const testItem = getTestItemByTestId(APP.store.getState, test_id);
            if (!testItem)
                return;
            if (testItem.type === '설문형') {
                const surveyData = getMergeSurveySaveById(APP.store.getState, test_id);
                await postSaveSubmitAnswer({
                    id: test_id,
                    props: {
                        subject_items: [],
                        answers: checkSurveyString(Object.values(surveyData?.answers)),
                    },
                });
            }
            else if (testItem.type === '파일첨부형') {
            }
            else {
                const omrFrequency = getOmrFrequencyByTestId(APP.store.getState(), test_id);
                await postSaveSubmitAnswer({
                    id: test_id,
                    props: {
                        subject_items: omrFrequency.subject_items,
                        answers: omrFrequency.answers,
                    },
                });
            }
        });
    }
    catch (error) {
        console.error({ error });
    }
    finally {
        APP.store.dispatch(toggleLoad(false));
    }
    if (websocket && e) {
        websocket.disconnect();
        websocket = null;
    }
};
let websocket = null;
MiddlewareRegistry.register(store => next => async (action) => {
    const { dispatch } = store;
    const roomId = getRoomOption(store.getState)?.uuid;
    switch (action.type) {
        case 'SET_SUBMIT_OPTION':
            const test_id = getFocusTestId(store.getState);
            getSubmitStatusAPI(test_id, '', action.data);
            break;
        case ON_ENTER_TEST_ROOM:
            // ★영구 로딩 방지: 이 케이스의 모든 조기 return/예외/타임아웃 경로에서
            // finally 가 반드시 로딩을 해제한다(70-80명 러시에서 서버 지연/실패 시
            // "입장 버튼 눌러도 로딩만 뜨고 무반응" 실측 — 각 실패는 알럿 후 버튼 화면 복귀).
            dispatch(toggleLoad(true));
            try {
                if (!roomId) {
                    return;
                }
                try {
                    await initTest(store, roomId, true);
                }
                catch (error) {
                    // initTest 실패가 이후 입장/websocket 흐름(로딩 해제 포함)을 막지 않도록 한다.
                    console.error('initTest(ON_ENTER_TEST_ROOM) failed:', error);
                }
                const searchResult = await APP.API.searchRoom({ uuid: roomId }, false, true).then(async (response) => {
                    if (response.complete) {
                        const { items } = response.data;
                        if (items && items.length > 0) {
                            const item = items[0];
                            if (item.status === 'closed') {
                                return 'ok';
                            }
                            return 'ended';
                        }
                        else {
                            return 'ok';
                        }
                    }
                    // 서버 무응답/네트워크/일시 오류 — "종료된 모의고사"로 단정하지 않는다
                    // (과거에는 이 경로도 종료 알럿+창 닫힘으로 둔갑했음)
                    return 'error';
                });
                if (searchResult === 'ended') {
                    alert('이미 종료된 모의고사입니다.');
                    dispatch(setIsEnterRoom(false));
                    APP.eventManager.publish('UPDATE_PAGE', {
                        page: PAGE_TYPE.ERROR,
                    });
                    self.close();
                    return;
                }
                if (searchResult === 'error') {
                    alert('입장에 실패했습니다. 네트워크 확인 후 다시 시도해주세요.');
                    dispatch(setIsEnterRoom(false));
                    return;
                }
                if (websocket) {
                    websocket.disconnect();
                    websocket = null;
                }
                const checkResponse = await checkOccupiedUser();
                if (checkResponse) {
                    if (!checkResponse.complete) {
                        alert(checkResponse.data);
                        return;
                    }
                    websocket = new MockTestWebsocket(disconnectWebsocket);
                    try {
                        // await 로 전환 — 연결 완료 전에 로딩이 풀려 재클릭되는 창을 없앤다
                        await websocket.connect(handlerMessage);
                        store.dispatch(setIsEnterRoom(true));
                    }
                    catch (err) {
                        console.log({ err });
                        websocket?.disconnect();
                        websocket = null;
                        alert('네트워크 연결이 불안정합니다. 다시 시도해주세요.');
                        dispatch(setIsEnterRoom(false));
                    }
                }
            }
            catch (error) {
                console.error('ON_ENTER_TEST_ROOM failed:', error);
                alert('입장에 실패했습니다. 잠시 후 다시 시도해주세요.');
                dispatch(setIsEnterRoom(false));
            }
            finally {
                dispatch(toggleLoad(false));
            }
            break;
        case SET_ROOM_JOINED:
            if (action.joined) {
                if (!roomId) {
                    store.dispatch(setTestBackground(undefined));
                    return;
                }
                await initTest(store, roomId, false);
                dispatch(setIsEnterRoom(true));
                alreadyDisconnect = false;
            }
            else {
                await disconnectWebsocket(undefined);
                alreadyDisconnect = true;
            }
            break;
        case ON_REFETCH_TEST_LIST:
            // 전체화면 로딩은 사용자가 직접 트리거한 흐름(showLoad=true, 본인 제출 등)에서만.
            // WS 브로드캐스트 발 백그라운드 갱신(남의 제출/시작)은 조용히 수행 —
            // "누군가 제출하면 방 전원에게 로딩" 문제의 직접 원인이었다.
            if (action.showLoad)
                dispatch(toggleLoad(true));
            if (!roomId) {
                dispatch(setIsEnterRoom(false));
                if (action.showLoad)
                    dispatch(toggleLoad(false));
                return;
            }
            try {
                await initTest(store, roomId, false, action.retryUnsubmitted, action.retryTestId);
            }
            finally {
                // initTest 가 어떤 이유로든 throw 해도 로딩 오버레이가 영구히 남지 않도록 보장
                if (action.showLoad)
                    dispatch(toggleLoad(false));
            }
            break;
        case LEAVE_ROOM:
            // 방 퇴장 시 대기 중인 부분갱신 타이머 정리(늦은 REST/디스패치 방지)
            clearSubmitStatusRefreshTimers();
            break;
        case ON_TEST_SUBMITTED: {
            // 누군가의 제출 신호 — 과거엔 전원 무필터 전체 재조회(initTest ≈ 테스트당 3-4 REST ×
            // 인원수)와 그로 인한 setTestList 참조 변경 → 학생 SET_FOCUS_USER 캐스케이드 로딩까지
            // 발생했다. 역할별 최소 처리로 축소한다.
            const payload = action.payload ?? {};
            const submittedTestId = payload.test_id;
            const submittedMemberId = payload.member_id;
            if (getIsTeacher(store.getState)) {
                // 교사: 제출 현황(제출수/목록)만 부분 갱신 — 교사 UI(제출현황 테이블/제출률)는
                // 전부 submitList 를 읽으므로 충분하다. 테스트별 1초 코얼레싱.
                scheduleSubmitStatusRefresh(store, submittedTestId);
                // 해당 학생의 상세(답안)를 열람 중이면 답안 상태도 갱신
                const focusUser = getFocusUser(store.getState);
                if (submittedTestId && submittedMemberId && focusUser?.id === submittedMemberId) {
                    getAnswerState(submittedTestId, submittedMemberId);
                }
                break;
            }
            // 학생: 남의 제출로 갱신할 데이터가 없다 — 본인 제출만 재조회(설문/파일첨부형의
            // '제출 완료' 화면 전환 담보). 페이로드에 member_id 가 없으면(서버 미포함 방어)
            // 기존 동작(재조회, 단 무로딩)으로 폴백해 화면 전환 회귀를 막는다.
            const selfId = getExternalUserId(store.getState);
            if (!submittedMemberId || submittedMemberId === selfId) {
                store.dispatch(onRefetchTestList());
            }
            break;
        }
        case SET_FOCUS_TEST_ID:
            if (!getIsTeacher(store.getState))
                return next(action);
            store.dispatch(setFocusUser(undefined));
            break;
        case SET_FOCUS_USER: {
            // 전체화면 로딩은 교사(학생 상세 열람)만 — 학생 쪽은 목록 갱신 리스너가 자동
            // 발화시키는 경로라, 로딩을 띄우면 백그라운드 갱신마다 화면이 덮인다.
            const showFocusLoad = getIsTeacher(store.getState);
            if (showFocusLoad)
                dispatch(toggleLoad(true));
            try {
                const selectedTestId = getFocusTestId(store.getState());
                const member_id = action?.user?.id;
                // 미제출 사용자 여부 파악. 학생 본인이 전체 타이머 종료(open→closed)로 넘어온 경우엔 서버
                // 강제제출 반영 지연을 감안해 재폴링(retryUnsubmitted)한다. 교사/평상시 조회는 기존 1회 그대로.
                if (member_id) {
                    const isSelfClosed = !getIsTeacher(store.getState) &&
                        getTestItemByTestId(store.getState(), selectedTestId)?.status === 'closed';
                    if (isSelfClosed) {
                        await getAnswerState(selectedTestId, member_id, 4, false, true);
                    }
                    else {
                        await getAnswerState(selectedTestId, member_id);
                    }
                }
            }
            catch (error) {
            }
            finally {
                if (showFocusLoad)
                    dispatch(toggleLoad(false));
            }
            break;
        }
        case 'SET_IS_ENTER_ROOM':
            if (!action.isEnterRoom) {
                await disconnectWebsocket(undefined);
                if (websocket) {
                    websocket.disconnect();
                    websocket = null;
                }
            }
            else {
                alreadyDisconnect = false;
            }
            break;
        case 'SET_OMR_TEMP_SAVE':
            if (getIsTeacher(store.getState))
                return next(action);
            const member_id = getExternalUserId(store.getState);
            const loadAnswerResponse = await getLoadAnswer({
                id: action.testId,
                props: {
                    member_id,
                },
            });
            const { subject_items, answers } = loadAnswerResponse?.result ?? {};
            store.dispatch(setOmrFrequency(action.testId, {
                server: { subject_items: subject_items || [], answers: answers || [] },
            }));
            break;
        case 'SET_SURVEY_TEMP_SAVE':
            if (getIsTeacher(store.getState))
                return next(action);
            const memberId = getExternalUserId(store.getState);
            const loadSurveyAnswerResponse = await getLoadAnswer({
                id: action.testId,
                props: {
                    member_id: memberId,
                },
            });
            const { subject_items: surveySelectedItem, answers: surveyAnswers } = loadSurveyAnswerResponse.result;
            store.dispatch(setSurveySave(action.testId, {
                server: { subject_items: surveySelectedItem || [], answers: surveyAnswers || [] },
            }));
            break;
    }
    return next(action);
});
